/**
 * HoverInsertZone — Notion-like insert affordance.
 *
 * Invisible by default. On hover reveals a subtle `+` button in the left
 * gutter and turns the zone into a click target that creates a new empty
 * block of `defaultType`. Focus goes to the new block via `focusBlockId`
 * (set in RequirementsPage.handleCreateBlock).
 *
 * When `emptyHint` is provided, renders a persistent (but quiet) discoverable
 * "Add X" button instead — used when a parent has no children yet.
 */
import { Plus } from 'lucide-react'
import type { RequirementSection } from '@ispot/shared'
import type { BlockType } from '../SlashMenu'
import type { CreateBlockInput } from '../AdderBlock'

interface Props {
  defaultType: BlockType
  parents: { story?: string; epic?: string }
  personaSessionId?: string
  /** Document section to stamp on a new epic created here. */
  section?: RequirementSection
  onCreate: (input: CreateBlockInput) => Promise<{ id: string } | void> | void
  /** Persistent hint shown when parent is empty. */
  emptyHint?: string
  /** Pixel offset for the gutter + button. Defaults to -26 (aligns with block controls). */
  gutterOffset?: number
}

export function HoverInsertZone({
  defaultType,
  parents,
  personaSessionId,
  section,
  onCreate,
  emptyHint,
  gutterOffset = -26,
}: Props) {
  const parentFor = (t: BlockType): string | undefined =>
    t === 'story' ? parents.story : undefined

  const add = () => {
    void onCreate({
      type: defaultType,
      name: '',
      parentId: parentFor(defaultType),
      personaSessionId: defaultType === 'story' ? personaSessionId : undefined,
      section: defaultType === 'epic' ? section : undefined,
    })
  }

  if (emptyHint) {
    return (
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 mt-0.5 rounded px-1.5 py-0.5 text-[12px] transition-colors hover:bg-warm-100"
        style={{ color: 'var(--color-fg-muted)', opacity: 0.55 }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.55')}
      >
        <Plus size={11} /> {emptyHint}
      </button>
    )
  }

  return (
    <div
      className="group/hz relative cursor-text"
      onClick={add}
      style={{ height: 10 }}
      aria-hidden
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Add ${defaultType}`}
        onClick={(e) => { e.stopPropagation(); add() }}
        onMouseDown={(e) => e.preventDefault()}
        className="absolute top-1/2 flex items-center justify-center rounded opacity-0 group-hover/hz:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-warm-100"
        style={{
          left: gutterOffset,
          transform: 'translateY(-50%)',
          width: 18,
          height: 18,
          color: 'var(--color-fg-muted)',
          background: 'transparent',
          border: 0,
          padding: 0,
        }}
      >
        <Plus size={11} />
      </button>
    </div>
  )
}
